import { flow, getEnv, types } from 'mobx-state-tree';

import { createMapWithTransform } from 'utils/store/createMap';
import { createBenchmarkModel } from './BenchmarkModel';
import { createBriefingModel } from './BriefingModel';
import FireModel, { createFireModel, FireModelType } from './FireModel';
import { ItemLoadingStateEnum, ListLoadingStateEnum } from './LoadingStateEnums';
import { createOrganizationModel } from './OrganizationModel';
import { createProjectModel } from './ProjectModel';
import { AdvancedStoreEnv } from './StoreEnv';

const FireStore = types
  .model('FireStore', {
    itemLoadingState: types.maybe(ItemLoadingStateEnum),
    listLoadingState: types.maybe(ListLoadingStateEnum),
    fire: types.maybe(FireModel),
    fires: types.maybe(types.map(FireModel))
  })
  .actions(self => {
    const getFires = flow(function*(organizationId: number) {
      const { client, applicationStore, dataStore } = getEnv<AdvancedStoreEnv>(self);

      try {
        self.listLoadingState = 'loading';
        const result: any = yield client.getFires(organizationId);

        if (Array.isArray(result)) {
          const list: FireModelType[] = [];
          for (const item of result) {
            const storeItem = createFireModel(item);
            if (storeItem) {
              list.push(storeItem);
            }
          }
          dataStore.setFires(list);
        }

        self.fires = createMapWithTransform(result, createFireModel);
        self.listLoadingState = undefined;
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error('FireStore | getFires', error, error.body);
        }

        if (client.isNotFound(error)) {
          self.listLoadingState = 'not_found';
          return undefined;
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.listLoadingState = 'load_error';

        return undefined;
      }
    });

    const getFire = flow(function*(fireId: number) {
      const { client, applicationStore, dataStore } = getEnv<AdvancedStoreEnv>(self);

      try {
        self.itemLoadingState = 'loading';
        const result: any = yield client.getFire(fireId);

        if (!result) {
          throw new Error('No response from server');
        }

        if (result.organization) {
          const organization = createOrganizationModel(result.organization);
          dataStore.addOrganization(organization);
          dataStore.setOrganization(organization);
        }

        if (result.project) {
          const project = createProjectModel(result.project);
          dataStore.addProject(project);
          dataStore.setProject(project);
        }

        if (Array.isArray(result.painpoints)) {
          for (const painpoint of result.painpoints) {
            dataStore.convertAndAddPainpoint(painpoint);
          }
        }

        if (Array.isArray(result.benchmarks)) {
          for (const benchmark of result.benchmarks) {
            const benchmarkModel = createBenchmarkModel(benchmark);
            dataStore.addBenchmark(benchmarkModel);
          }
        }

        if (Array.isArray(result.hypothesis)) {
          for (const hypothesis of result.hypothesis) {
            dataStore.convertAndAddHypothesis(hypothesis);
          }
        }

        if (result.briefing) {
          const briefing = createBriefingModel(result.briefing);
          dataStore.setBriefing(briefing);
        }

        self.fire = createFireModel(result);
        dataStore.setFire(self.fire);

        self.itemLoadingState = undefined;
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error('FireStore | getFire', error, error.body);
        }

        if (client.isNotFound(error)) {
          self.itemLoadingState = 'not_found';
          return undefined;
        }


        if (client.isAccessDenied(error)) {
          console.log('access_denied');
          self.itemLoadingState = 'access_denied';
          throw new Error('Access denied');
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.itemLoadingState = 'load_error';
        return undefined;
      }
    });


    const createFire = flow(function*(patch: any, organizationId: number) {
      const { client, applicationStore, dataStore } = getEnv<AdvancedStoreEnv>(self);

      try {
        self.itemLoadingState = 'saving';
        const result: any = yield client.createFire(patch, organizationId);

        self.fire = createFireModel(result);
        dataStore.setFire(self.fire);
        self.itemLoadingState = undefined;
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error('FireStore | createFire', error, error.body);
        }

        if (client.isNotFound(error)) {
          self.itemLoadingState = 'not_found';
          return undefined;
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.itemLoadingState = 'save_error';

        return undefined;
      }
    });

    const generateMorePainpoints = flow(function*(fireId: number, clusterId: number) {
      const { client, applicationStore } = getEnv<AdvancedStoreEnv>(self);

      try {
        yield client.generateMorePainpointsFire(fireId, clusterId);
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error('FireStore | generateMorePainpoints', error, error.body);
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.itemLoadingState = 'load_error';
        return undefined;
      }
    });

    const generateMoreBenchmarks = flow(function*(fireId: number) {
      const { client, applicationStore } = getEnv<AdvancedStoreEnv>(self);

      try {
        yield client.generateMoreBenchmarksFire(fireId);
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error('FireStore | generateMoreBenchmarks', error, error.body);
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.itemLoadingState = 'load_error';
        return undefined;
      }
    });

    const generateMoreHypotheses = flow(function*(fireId: number) {
      const { client, applicationStore } = getEnv<AdvancedStoreEnv>(self);

      try {
        yield client.generateMoreHypothesesFire(fireId);
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error('FireStore | generateMoreHypotheses', error, error.body);
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.itemLoadingState = 'load_error';
        return undefined;
      }
    });

    const convertFireToProject = flow(function*(fireId: number) {
      const { client, applicationStore, dataStore } = getEnv<AdvancedStoreEnv>(self);

      try {
        const result = yield client.convertFireToProject(fireId);

        if (result.project) {
          const project = createProjectModel(result.project);
          dataStore.addProject(project);
          dataStore.setProject(project);
        }

        self.itemLoadingState = undefined;
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error('FireStore | convertFireToProject', error, error.body);
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.itemLoadingState = 'load_error';
        return undefined;
      }
    });

    const retryTask = flow(function*(fireId: number) {
      const { client, applicationStore } = getEnv<AdvancedStoreEnv>(self);

      try {
        yield client.retryTask(fireId);
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error('FireStore | retryTask', error, error.body);
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.itemLoadingState = 'load_error';
        return undefined;
      }
    });

    const deleteFire = flow(function*(fireId: number) {
      const { client, applicationStore, dataStore } = getEnv<AdvancedStoreEnv>(self);

      try {
        self.itemLoadingState = undefined;
        yield client.deleteFire(fireId);

        dataStore.deleteFire(fireId);

        self.fire = undefined;
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error('FireStore | deleteFire', error, error.body);
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.itemLoadingState = 'load_error';
        return undefined;
      }
    });

    const deleteFirePowerpoint = flow(function*(fireId: number, projectId: number) {
      const { client, applicationStore, dataStore } = getEnv<AdvancedStoreEnv>(self);

      try {
        yield client.deletePowerpoint(projectId);

        if (self.fire) {
          self.fire.clearPowerpointUrl();
        }
        if (dataStore.fire) {
          dataStore.fire.clearPowerpointUrl();
        }
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error('FireStore | deleteFirePowerpoint', error, error.body);
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.itemLoadingState = 'load_error';
        return undefined;
      }
    });

    return {
      createFire,
      getFires,
      getFire,
      generateMorePainpoints,
      generateMoreBenchmarks,
      generateMoreHypotheses,
      convertFireToProject,
      retryTask,
      deleteFire,
      deleteFirePowerpoint
    };
  }).views((self) => {
    return {
      get isItemLoading(): boolean {
        return (
          self.itemLoadingState === 'loading' ||
          self.itemLoadingState === 'deleting'
        );
      },
      get isItemSaving(): boolean {
        return self.itemLoadingState === 'saving';
      },
      get isItemNotFound(): boolean {
        return self.itemLoadingState === 'not_found';
      },
      get isItemSaveError(): boolean {
        return (
          self.itemLoadingState === 'save_error' ||
          self.itemLoadingState === 'delete_error'
        );
      },
      get isItemLoadError(): boolean {
        return (
          self.itemLoadingState === 'load_error' ||
          self.itemLoadingState === 'access_denied' ||
          self.itemLoadingState === 'feature_disabled'
        );
      },

      get isListLoading(): boolean {
        return self.listLoadingState === 'loading';
      },
      get isListDeleting(): boolean {
        return self.listLoadingState === 'deleting';
      },
      get isListBusy(): boolean {
        return this.isListLoading || this.isListDeleting;
      },
      get isListError(): boolean {
        return (
          self.listLoadingState === 'load_error' ||
          self.listLoadingState === 'access_denied' ||
          self.listLoadingState === 'feature_disabled'
        );
      }
    };
  });

export type FireStoreType = typeof FireStore.Type;
export default FireStore;
